import React, { useRef, useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ZoomIn, ZoomOut, RotateCcw, Eye, EyeOff } from 'lucide-react';

const DrawingCanvas = ({ 
  points = [], 
  contourLines = [], 
  triangles = [],
  showPointLabels = true,
  showTriangulation = false,
  showContours = true 
}) => {
  const canvasRef = useRef(null);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [lastMousePos, setLastMousePos] = useState({ x: 0, y: 0 });

  useEffect(() => {
    drawCanvas();
  }, [points, contourLines, triangles, scale, offset, showPointLabels, showTriangulation, showContours]);

  const drawCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    
    // Configurar canvas para alta resolución
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    ctx.scale(dpr, dpr);
    
    // Limpiar canvas
    ctx.clearRect(0, 0, rect.width, rect.height);
    
    if (points.length === 0) {
      // Mostrar mensaje cuando no hay puntos
      ctx.fillStyle = '#9CA3AF';
      ctx.font = '16px sans-serif';
      ctx.textAlign = 'center';
      ctx.fillText('Agrega puntos para ver el croquis', rect.width / 2, rect.height / 2);
      return;
    }

    // Calcular límites y escala automática
    const bounds = calculateBounds(points);
    const autoScale = calculateAutoScale(bounds, rect.width, rect.height);
    
    // Aplicar transformaciones
    ctx.save();
    ctx.translate(rect.width / 2 + offset.x, rect.height / 2 + offset.y);
    ctx.scale(scale * autoScale, -scale * autoScale); // Y invertido para coordenadas cartesianas
    ctx.translate(-bounds.centerX, -bounds.centerY);

    // Dibujar triangulación si está habilitada
    if (showTriangulation && triangles.length > 0) {
      drawTriangulation(ctx, triangles);
    }

    // Dibujar curvas de nivel
    if (showContours && contourLines.length > 0) {
      drawContourLines(ctx, contourLines);
    }

    // Dibujar puntos
    drawPoints(ctx, points, showPointLabels, scale * autoScale);

    ctx.restore();
  };

  const calculateBounds = (points) => {
    const xs = points.map(p => p.x);
    const ys = points.map(p => p.y);
    
    const minX = Math.min(...xs);
    const maxX = Math.max(...xs);
    const minY = Math.min(...ys);
    const maxY = Math.max(...ys);
    
    return {
      minX, maxX, minY, maxY,
      width: maxX - minX,
      height: maxY - minY,
      centerX: (minX + maxX) / 2,
      centerY: (minY + maxY) / 2
    };
  };

  const calculateAutoScale = (bounds, canvasWidth, canvasHeight) => {
    const margin = 50;
    const scaleX = (canvasWidth - margin * 2) / bounds.width;
    const scaleY = (canvasHeight - margin * 2) / bounds.height;
    return Math.min(scaleX, scaleY, 1);
  };

  const drawPoints = (ctx, points, showLabels, currentScale) => {
    points.forEach(point => {
      // Dibujar punto
      ctx.fillStyle = '#EF4444';
      ctx.beginPath();
      ctx.arc(point.x, point.y, 3 / currentScale, 0, 2 * Math.PI);
      ctx.fill();
      
      // Dibujar etiqueta si está habilitada
      if (showLabels) {
        ctx.save();
        ctx.scale(1 / currentScale, -1 / currentScale);
        ctx.fillStyle = '#1F2937';
        ctx.font = '12px sans-serif';
        ctx.textAlign = 'center';
        ctx.fillText(
          `${point.id || 'P'}`,
          point.x * currentScale,
          -point.y * currentScale - 8
        );
        ctx.fillStyle = '#6B7280';
        ctx.font = '10px sans-serif';
        ctx.fillText(
          `Z: ${point.z.toFixed(2)}`,
          point.x * currentScale,
          -point.y * currentScale + 15
        );
        ctx.restore();
      }
    });
  };

  const drawTriangulation = (ctx, triangles) => {
    ctx.strokeStyle = '#D1D5DB';
    ctx.lineWidth = 1;
    
    triangles.forEach(triangle => {
      const [p1, p2, p3] = triangle;
      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.lineTo(p3.x, p3.y);
      ctx.closePath();
      ctx.stroke();
    });
  };

  const drawContourLines = (ctx, contourLines) => {
    contourLines.forEach(contour => {
      // Color basado en el nivel
      const hue = (contour.level * 137.5) % 360;
      ctx.strokeStyle = `hsl(${hue}, 70%, 50%)`;
      ctx.lineWidth = 2;
      
      contour.lines.forEach(line => {
        ctx.beginPath();
        ctx.moveTo(line.start.x, line.start.y);
        ctx.lineTo(line.end.x, line.end.y);
        ctx.stroke();
      });
    });
  };

  const handleMouseDown = (e) => {
    setIsDragging(true);
    setLastMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const deltaX = e.clientX - lastMousePos.x;
    const deltaY = e.clientY - lastMousePos.y;
    
    setOffset(prev => ({
      x: prev.x + deltaX,
      y: prev.y + deltaY
    }));
    
    setLastMousePos({ x: e.clientX, y: e.clientY });
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? 0.9 : 1.1;
    setScale(prev => Math.max(0.1, Math.min(5, prev * delta)));
  };

  const resetView = () => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
  };

  return (
    <motion.div 
      className="bg-white/90 backdrop-blur-xl border border-gray-200/50 rounded-3xl shadow-xl overflow-hidden"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
    >
      <div className="p-4 border-b border-gray-200/50 flex justify-between items-center">
        <h3 className="text-lg font-bold text-gray-800">Croquis Topográfico</h3>
        <div className="flex gap-2">
          <motion.button
            onClick={() => setScale(prev => Math.min(5, prev * 1.2))}
            className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ZoomIn className="w-4 h-4" />
          </motion.button>
          <motion.button
            onClick={() => setScale(prev => Math.max(0.1, prev * 0.8))}
            className="p-2 bg-blue-100 text-blue-600 rounded-lg hover:bg-blue-200 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <ZoomOut className="w-4 h-4" />
          </motion.button>
          <motion.button
            onClick={resetView}
            className="p-2 bg-gray-100 text-gray-600 rounded-lg hover:bg-gray-200 transition-colors"
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
          >
            <RotateCcw className="w-4 h-4" />
          </motion.button>
        </div>
      </div>
      
      <div className="relative">
        <canvas
          ref={canvasRef}
          className="w-full h-96 cursor-move"
          onMouseDown={handleMouseDown}
          onMouseMove={handleMouseMove}
          onMouseUp={handleMouseUp}
          onMouseLeave={handleMouseUp}
          onWheel={handleWheel}
          style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
        />
        
        <div className="absolute bottom-4 right-4 text-xs text-gray-500 bg-white/80 px-2 py-1 rounded">
          Escala: {(scale * 100).toFixed(0)}%
        </div>
      </div>
    </motion.div>
  );
};

export default DrawingCanvas;